import { useTranslation } from "react-i18next";

/**
 * What someone sees when a screen crashes, or when a route does not exist.
 *
 * Without this the router falls back to TanStack's own default: the English
 * words "Something went wrong" on a bare white page, no way back, and — because
 * the SPA owns the whole window — no browser chrome to escape with either. The
 * only way out was the back button or retyping the URL.
 *
 * Deliberately plain: it has to render when the design system, a token file or a
 * feature component is the thing that just broke, so it leans on `.wl-*` classes
 * and tokens but survives without them.
 */
export function AppErrorScreen({ error, notFound = false }: { error?: unknown; notFound?: boolean }) {
  const { t } = useTranslation();
  const detail =
    error instanceof Error ? error.message : typeof error === "string" ? error : null;

  return (
    <div
      data-screen-label="Back-office Error"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6, 24px)",
        background: "var(--surface-canvas, #F4F6F9)",
        color: "var(--text-primary, #161B2B)",
      }}
    >
      <div style={{ maxWidth: "440px", textAlign: "center" }}>
        <h1 className="t-h2" style={{ marginBottom: "var(--space-2, 8px)" }}>
          {notFound ? t("error.notFoundTitle") : t("error.crashTitle")}
        </h1>
        <p className="t-body" style={{ color: "var(--text-secondary, #515869)", marginBottom: "var(--space-5, 20px)" }}>
          {notFound ? t("error.notFoundBody") : t("error.crashBody")}
        </p>
        <div className="wl-hstack" style={{ gap: "var(--space-2, 8px)", justifyContent: "center" }}>
          <a className="wl-btn wl-btn--primary" href="/dashboard">
            {t("error.backToStart")}
          </a>
          {!notFound && (
            <button type="button" className="wl-btn wl-btn--secondary" onClick={() => window.location.reload()}>
              {t("error.reload")}
            </button>
          )}
        </div>
        {/* The message itself, for a screenshot to support. Not a stack trace:
            that says nothing to the reader and everything to a stranger. */}
        {detail && (
          <p
            className="t-caption"
            style={{ marginTop: "var(--space-5, 20px)", color: "var(--text-tertiary, #6B7385)", wordBreak: "break-word" }}
          >
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}
